import React from 'react';
import { AlertTriangle, CheckCircle2, Info, ShieldAlert } from 'lucide-react';
import { DataQualityWarning, UploadedDataset, getDataQualityWarnings } from '../../services/analytics';

interface DataQualityPanelProps {
  dataset: UploadedDataset;
  compact?: boolean;
}

const DataQualityPanel: React.FC<DataQualityPanelProps> = ({ dataset, compact = false }) => {
  const warnings = getDataQualityWarnings(dataset);

  return (
    <div className="premium-card" style={{ padding: compact ? '1rem' : '1.25rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', marginBottom: warnings.length ? '1rem' : 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span className="icon-tile" style={{ width: '36px', height: '36px' }}>
            {warnings.length ? <ShieldAlert size={18} /> : <CheckCircle2 size={18} />}
          </span>
          <div>
            <h3 className="section-title">Data quality</h3>
            <p style={{ margin: 0, fontSize: '0.88rem' }}>
              {warnings.length ? `${warnings.length} item${warnings.length === 1 ? '' : 's'} to review before analysis.` : 'No major data quality issues detected.'}
            </p>
          </div>
        </div>
        <span className="soft-chip" style={{ minHeight: '32px', padding: '0.35rem 0.65rem' }}>
          {dataset.rows.length.toLocaleString()} rows
        </span>
      </div>

      {warnings.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: compact ? '1fr' : 'repeat(auto-fit, minmax(240px, 1fr))', gap: '0.75rem' }}>
          {warnings.map((warning) => (
            <QualityItem key={warning.id} warning={warning} />
          ))}
        </div>
      )}
    </div>
  );
};

const QualityItem: React.FC<{ warning: DataQualityWarning }> = ({ warning }) => {
  const Icon = warning.severity === 'danger' ? AlertTriangle : warning.severity === 'warning' ? ShieldAlert : Info;
  const color = warning.severity === 'danger'
    ? 'var(--status-danger)'
    : warning.severity === 'warning'
      ? 'var(--gabmic-orange)'
      : 'var(--text-secondary)';

  return (
    <div
      style={{
        display: 'flex',
        gap: '0.7rem',
        alignItems: 'flex-start',
        padding: '0.85rem',
        border: '1px solid var(--border-color)',
        borderRadius: '8px',
        background: 'rgba(255, 255, 255, 0.025)',
      }}
    >
      <Icon size={18} color={color} style={{ flex: '0 0 auto', marginTop: '0.1rem' }} />
      <div>
        <strong style={{ display: 'block', fontSize: '0.9rem', marginBottom: '0.2rem' }}>{warning.title}</strong>
        <p style={{ margin: 0, fontSize: '0.84rem' }}>{warning.detail}</p>
      </div>
    </div>
  );
};

export default DataQualityPanel;
